import React, { useEffect, useMemo, useState } from 'react';
import { ICONS, PATHS } from 'src/common/constants';
import Button from '../Button';
import Empty from '../Empty';
import Icon from '../Icon';
import Table from 'src/components/Table';
import { PAGINATION_KEYS } from 'src/store/slices/persistedUiSlice';
import { useSelector } from 'react-redux';
import { selectSearch } from 'src/store/selectors/search';
import LoadState from 'src/components/LoadState/LoadState';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { selectFormLoader } from 'src/store/selectors/FormLoader';

const SKELETON_ROW_COUNT: number = 13;
import { IContactAdvancedFilters } from 'src/pages/Contacts/CustomHooks/useContactAdvancedFilters';
import { IContactTableRow, transformContactData } from './data';
import { createContactsTableColumns } from 'src/components/ContactsTable/helpers';
import ContactsTableActions from 'src/components/ContactsTable/ContactsTableActions';
import ContactPropertyDetailsDialogue from 'src/components/ContactPropertyDialogue/ContactPropertyDialogue';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { ITableColumn } from 'src/components/Table/types';
import { useContactsSuiteActionsDataFromContext } from 'src/pages/Contacts/ContactsContext/ContactsContext';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import ContactForm from 'src/components/ContactForm/ContactForm';
import ChangeUserRoleDialogue from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/ChangeUserRoleDialogue';
import ChangeEmailDialogue from 'src/components/ChangeEmailDialogue/ChangeEmailDialogue';
import { useChangeUserEmailOrchestrator } from 'src/components/ContactFormComponents/ChangeUserEmailDialogue/CustomHooks/useChangeUserEmailOrchestrator';
import './ContactsTable.scss';
import {
  useContactFormSubmissionContacts,
  UseContactFormSubmissionContactsReturn,
} from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/useContactFormSubmission';
import { enrichContactDataForEdit } from 'src/utils/CommonHelpers/commonhelpers';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import {
  useTablePagination,
  UseTablePaginationReturn,
} from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import useProperties from 'src/utils/CommonDataFetchingUtils/useProperties';

interface ContactsTableProps {
  contacts: IContact[];
  onDeleteContact: (data: any) => void;
  sendResetPasswordLinkToUser: (data: any) => void;
  sendActivationLinkToUser: (data: any) => void;
  onSelectContact: (userId: any) => void;
  selectedContacts: any[];
  onSendInvitation: () => void;
  onShowFilterModal?: () => void;
  activeFilters: IContactAdvancedFilters;
  resetFilters: any;
  removeSingleActiveFilter: any;
  hasActiveFilters: boolean;
  refetchContactFormData: any;
  allSelected: any;
  onSelectAllContacts: any;
  isLoadingContacts: boolean;
  basicContactFilterString: string;
  updateBasicContactFilterString: (value: string) => void;
  canEditContactEmail: boolean;
}

const ContactsTable = ({
  contacts = [],
  onDeleteContact,
  sendResetPasswordLinkToUser,
  sendActivationLinkToUser,
  onSelectContact,
  selectedContacts = [],
  onSendInvitation,
  onShowFilterModal,
  activeFilters,
  resetFilters,
  removeSingleActiveFilter,
  hasActiveFilters,
  refetchContactFormData,
  allSelected,
  onSelectAllContacts,
  isLoadingContacts,
  basicContactFilterString,
  updateBasicContactFilterString,
  canEditContactEmail,
}: ContactsTableProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [roleChangeContact, setRoleChangeContact] = useState<any>(null);
  const [emailChangeContact, setEmailChangeContact] = useState<any>(null);
  const [selectedContactPropertyDetails, setSelectedContactPropertyDetails] =
    useState<IContact | null>(null);

  const emailChangeViewModel = useChangeUserEmailOrchestrator({
    contactId: emailChangeContact?.id ?? '',
    currentEmail: emailChangeContact?.email ?? '',
  });

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    contact: any | null;
  }>({
    isOpen: false,
    contact: null,
  });

  const updateSelectedContactPropertyDetails = (contactToSelect: IContact) => {
    setSelectedContactPropertyDetails(contactToSelect);
  };

  const handleCloseForm = () => {
    setShowOverlay(false);
    clearContactFormData();
  };

  const { itemsPerPage, onItemsPerPageChange }: UseTablePaginationReturn =
    useTablePagination();

  const {
    clearContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  const { handleSubmitContactForm }: UseContactFormSubmissionContactsReturn =
    useContactFormSubmissionContacts({
      onSuccess: () => refetchContactFormData(),
      onClose: handleCloseForm,
    });

  const { isLoading: isLoadingSuiteActions } =
    useContactsSuiteActionsDataFromContext();

  const search = useSelector(selectSearch);
  const loader = useSelector(selectFormLoader);

  const {
    data: properties,
    error: propertiesError,
    fetchProperties,
  } = useProperties();

  useEffect(() => {
    void fetchProperties();
  }, []);

  const handleAddContact = () => {
    setShowOverlay(true);
  };

  const handleEditContact = (contactData: any) => {
    setShowOverlay(true);
    const enrichedContactData: any = enrichContactDataForEdit(contactData);
    initializeContactFormDataFromEdit(enrichedContactData);
  };

  const handleOpenDeleteModal = (contactData: any) => {
    setDeleteModalState({
      isOpen: true,
      contact: contactData,
    });
  };

  const handleFinalDelete = () => {
    if (deleteModalState.contact) {
      onDeleteContact(deleteModalState.contact);
      setDeleteModalState({ isOpen: false, contact: null });
    }
  };

  const tableColumns: ITableColumn[] = useMemo(
    () =>
      createContactsTableColumns({
        selectedContacts,
        onSelectContact,
        handleEditContact,
        onDeleteContact: handleOpenDeleteModal,
        PATHS,
        ICONS,
        updateSelectedContactPropertyDetails,
        allSelected,
        onSelectAllContacts,
        properties,
        propertiesError,
        onEditRole: setRoleChangeContact,
        onEditEmail: setEmailChangeContact,
        canEditContactEmail,
      }),
    [
      selectedContacts,
      onSelectContact,
      onDeleteContact,
      handleEditContact,
      properties,
      propertiesError,
      canEditContactEmail,
    ]
  );

  const transformedData: IContactTableRow[] = useMemo(() => {
    if (!contacts || contacts.length === 0) {
      return [];
    }
    return transformContactData(contacts);
  }, [contacts]);

  const hasContacts: boolean = Array.isArray(contacts) && contacts.length > 0;

  const isLoading: boolean =
    loader || isLoadingContacts || isLoadingSuiteActions;
  const isEmpty: boolean = !isLoading && !hasContacts;

  const skeletonColumns: ITableColumn[] = useMemo(
    () =>
      tableColumns.map((c: ITableColumn) => ({
        ...c,
        cell: () => <Skeleton variant="text" width="70%" />,
      })),
    [tableColumns]
  );

  const skeletonData: any[] = useMemo(
    () =>
      Array.from({ length: SKELETON_ROW_COUNT }, (_: unknown, i: number) => ({
        id: `contacts-skeleton-${i}`,
      })),
    []
  );

  const emptyNode: React.ReactNode = (
    <Empty
      direction="ttb"
      headline={hasActiveFilters ? 'No results found' : 'No contacts yet'}
      strapline={
        hasActiveFilters
          ? 'Please update your filters and try again.'
          : "Keep all your contacts here so it's easier for you to manage."
      }
      searchIsActive={search && search.contacts ? search.contacts : ''}
      cta={
        hasActiveFilters ? (
          <Button
            color="teal"
            label="Clear Filters"
            onClick={resetFilters}
            icon={<Icon glyph="filter" weight="light" size="small" />}
            size="small"
          />
        ) : (
          <Button
            color="teal"
            label="Add Contact"
            onClick={handleAddContact}
            icon={<Icon glyph="user" weight="light" size="small" />}
            size="small"
          />
        )
      }
      image={<img alt="No contacts found" src="/assets/empty/contacts.svg" />}
    />
  );

  return (
    <>
      <ContactsTableActions
        onAddContact={handleAddContact}
        onShowFilterModal={onShowFilterModal}
        hasActiveFilters={hasActiveFilters}
        resetFilters={resetFilters}
        selectedContacts={selectedContacts}
        contacts={contacts}
        onSendInvitation={onSendInvitation}
        activeFilters={activeFilters}
        removeSingleActiveFilter={removeSingleActiveFilter}
        basicContactFilterString={basicContactFilterString}
        updateBasicContactFilterString={updateBasicContactFilterString}
        isLoading={isLoading}
      />
      <LoadState isLoading={false} isEmpty={isEmpty} emptyNode={emptyNode}>
        <Table
          columns={isLoading ? skeletonColumns : tableColumns}
          data={isLoading ? skeletonData : transformedData}
          paginate={{
            itemsPerPage,
            position: 'both',
            persistKey: PAGINATION_KEYS.contacts,
          }}
          paginationSkeleton={isLoading}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      </LoadState>
      {deleteModalState.isOpen && (
        <ConfirmationModal
          variant="danger"
          title={`Delete Contact ${deleteModalState.contact?.firstname}?`}
          confirmLabel="Yes, Delete Contact"
          cancelLabel="Go Back"
          requireTypeToConfirm={true}
          confirmationWord="DELETE"
          message={
            <div>
              <p>
                You are about to <strong>permanently delete</strong> this
                contact.
              </p>
              <p style={{ marginTop: '8px' }}>
                Doing so will immediately remove{' '}
                <strong>
                  {deleteModalState.contact?.firstname}{' '}
                  {deleteModalState.contact?.lastname}
                </strong>{' '}
                from the system, including:
              </p>
              <ul
                style={{
                  textAlign: 'left',
                  display: 'inline-block',
                  marginTop: '8px',
                }}
              >
                <li>All portal login and dashboard access</li>
                <li>Associated activity and history logs</li>
                <li>Personal profile and contact details</li>
              </ul>
            </div>
          }
          warningMessage="This action cannot be undone. Access will be revoked immediately."
          onCancel={() => setDeleteModalState({ isOpen: false, contact: null })}
          onConfirm={handleFinalDelete}
        />
      )}

      <ContactPropertyDetailsDialogue
        isOpen={!!selectedContactPropertyDetails}
        onClose={() => setSelectedContactPropertyDetails(null)}
        contactData={selectedContactPropertyDetails}
      />
      {showOverlay && (
        <ContactForm
          onClose={handleCloseForm}
          onSubmit={handleSubmitContactForm}
          sendResetPasswordLinkToUser={sendResetPasswordLinkToUser}
          sendActivationLinkToUser={sendActivationLinkToUser}
          pageUsedFrom="Contacts"
          onRoleChangeSuccess={refetchContactFormData}
        />
      )}
      {roleChangeContact && (
        <ChangeUserRoleDialogue
          isOpen
          contact={{
            id: roleChangeContact.id,
            firstname: roleChangeContact.firstname,
            lastname: roleChangeContact.lastname,
            profileImage: roleChangeContact.profileImage,
            userRole: roleChangeContact.roles?.[0]?.role?.name ?? '',
          }}
          onClose={() => setRoleChangeContact(null)}
          onSuccess={() => {
            setRoleChangeContact(null);
            refetchContactFormData?.();
          }}
        />
      )}
      <ChangeEmailDialogue
        isOpen={!!emailChangeContact}
        onClose={() => setEmailChangeContact(null)}
        viewModel={emailChangeViewModel}
        title="Change email address"
        description={
          emailChangeContact
            ? `Update the email address for ${`${emailChangeContact.firstname} ${emailChangeContact.lastname}`.trim()}.`
            : undefined
        }
        note="A verification link will be sent to the new address. The email changes only after the contact confirms it, so this row keeps showing the current address until then."
        successHeading="Verification link sent"
        successBody={
          <>
            We emailed a verification link to{' '}
            <span className="change-email-dialogue-success-email">
              {emailChangeViewModel.newEmail}
            </span>
            . Their email updates only once they confirm it.
          </>
        }
        avatar={
          emailChangeContact?.profileImage ? (
            <img
              src={emailChangeContact.profileImage}
              alt={`${emailChangeContact.firstname} ${emailChangeContact.lastname}`.trim()}
              className="change-email-dialogue-avatar-img"
            />
          ) : undefined
        }
      />
    </>
  );
};

export default ContactsTable;
